import {
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AutoAwesome as SparklesIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useTranslationApp } from "../../../lib/i18next";
import { useSelectorApp } from "../../../lib/redux";
import {
  MODAL_CREATE_WITH_IA_CONFIGURATION_ADVANCED,
  MODAL_CREATE_WITH_IA_CONFIGURATION_BASIC,
  MODAL_CREATE_WITH_IA_CONFIGURATION_INSTRUCTIONS,
  MODAL_CREATE_WITH_IA_PROMPT_OPTIONAL,
  MODAL_CREATE_WITH_IA_GENERATE_WITH_AI,
  MODAL_CREATE_WITH_IA_GENERATING,
  MODAL_CREATE_WITH_IA_LABEL_LANGUAGE,
  MODAL_CREATE_WITH_IA_LABEL_ACCENT,
  MODAL_CREATE_WITH_IA_LABEL_LOCATION,
  MODAL_CREATE_WITH_IA_LABEL_MEDIA_TYPE,
  MODAL_CREATE_WITH_IA_LABEL_VIDEO_TYPE,
  MODAL_CREATE_WITH_IA_LABEL_PLATFORM,
  MODAL_CREATE_WITH_IA_SUBTITLE,
  MODAL_CREATE_WITH_IA_TITLE,
  TEXT_IN_IMAGE,
  VIDEO_LANGUAGE,
  LOGO_IN_IMAGE,
  LOGO_IN_VIDEO,
  FIELD_NUMBER_GREATER_THAN,
  FIELD_REQUIRED,
  FIELD_NUMBER_0_OR_NEGATIVE,
  ASPECT_RATIO_PLACEHOLDER,
  CREATIVES_REMAINING_SHORT,
  CREATIVES_REMAINING_SHORT_SINGULAR,
  VIDEOS_REMAINING_SHORT,
  VIDEOS_REMAINING_SHORT_SINGULAR,
  MODAL_CREATE_WITH_IA_LABEL_WITH_SUBTITLES,
  MODAL_CREATE_WITH_IA_LABEL_AI_WATERMARK,
  MODAL_CREATE_WITH_IA_AI_WATERMARK_DESCRIPTION,
  MODAL_SUBTITLE_OPTIONS_PERSONALIZE,
  MODAL_CREATE_WITH_IA_LABEL_NUMBER_OF_IMAGES,
  MODAL_CREATE_WITH_IA_PLACEHOLDER_NUMBER_OF_IMAGES,
  SECTION_COUNTRY_DEFAULT_VALUE,
  LOGO_DESCRIPTION,
  LOGO_IN_VIDEO_DESCRIPTION_LEAD,
  MODAL_CREATE_WITH_IA_VIDEO_DURATION_20_SECONDS,
  MODAL_CREATE_WITH_IA_VIDEO_DURATION_30_SECONDS,
  MODAL_CREATE_WITH_IA_LABEL_VIDEO_DURATION_SECONDS,
  MODAL_CREATE_WITH_IA_SERVICE_VIDEO_LOGO_OPTIONAL_DESCRIPTION,
} from "../../../i18n/keysTranslations";
import {
  COUNTRIES,
  MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE,
  MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO,
  MODAL_CREATE_WITH_IA_TEXT_LANGUAGE_CODES_IMAGE,
  MODAL_CREATE_WITH_IA_TEXT_LANGUAGE_CODES_VIDEO,
  PLAN_TYPE_PACKAGES,
} from "../../../utils/constants";
import { BUSINESS_LOOK_AND_FEEL_PLACEHOLDER } from "../../../i18n/keysTranslations";
import {
  BUTTON_UPLOAD_LOGO,
  BUTTON_CHANGE_LOGO,
} from "../../../i18n/keysTranslations";
import TextField from "../../Form/TextField";
import Select from "../../Form/Select";
import AutocompleteField from "../../Form/AutocompleteField";
import Button from "../../Buttons/Button";
import Switch from "../../Form/Switch";
import CardMedia from "../../Media/CardMedia";
import UploadFileButton from "../../Buttons/UploadFileButton";
import {
  BUSINESS_OFFERING_PHYSICAL_PRODUCT,
  BUSINESS_OFFERING_SERVICES,
  MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT,
  MODAL_CREATE_WITH_IA_MEDIA_TYPES,
  MODAL_CREATE_WITH_IA_PLATFORMS,
  LOGO_IMAGE_FORMATS,
} from "../../../utils/constants";
import { useState, useEffect, useMemo, useCallback } from "react";
import ReferenceImageSelector from "./ReferenceImageSelector";
import ReferenceAdSelector from "./ReferenceAdSelector";
import {
  optionsLanguage,
  optionsVideoTypesForPhysicalProduct,
  optionsVideoTypesForServices,
  GENERIC_ACCENTS,
  getDefaultAccentForLanguage,
} from "../../../utils/options";
import ModalGalleryFromUrl, {
  MODAL_GALLERY_FETCH_MODE_URL,
} from "../ModalGalleryFromUrl";
import ModalCompetitors, {
  MODAL_COMPETITORS_FROM_CREATE_WITH_IA,
} from "../ModalCompetitors";
import ModalSubtitleOptions from "./ModalSubtitleOptions";
import AdditionalAssetsToggleSection from "./AdditionalAssetsToggleSection";
import { getExtensionFile } from "../../../utils/string";
import { useCompetitorScopeGuard } from "../../../hooks/useCompetitorScopeGuard";
import {
  CONFIG_PANEL_SCROLL_CONTAINER_ID,
  REFERENCE_AD_SECTION_ID,
  SERVICE_VIDEO_LOGO_SECTION_ID,
  scrollToConfigPanelSection,
} from "./utils/configPanelScroll";
import {
  getCreateWithIAReferenceImageLimit,
  getRemainingReferenceImageSlots,
  mergeReferenceImageUrls,
} from "../../../utils/createWithIAReferenceImages";

const ConfigurationPanel = ({
  control,
  errors,
  setValue,
  getValues,
  watch,
  activeTab,
  setActiveTab,
  isGenerating,
  onSubmit,
  onOpenGallery,
  onOpenGalleryForReferenceAd = () => {},
  isGalleryOpen,
  onToggleConfig,
  businessOffering,
  mediaType,
  logoUrl,
  userID,
  onSaveLogo,
  onChangeBrandInformation = () => {},
  defaultAdvancedAccordionExpanded = false,
  additionalAssets = [],
  additionalAssetsEnabledMap = {},
  onToggleAdditionalAsset = () => {},
  brandInformation = {},
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isUrlGalleryOpen, setIsUrlGalleryOpen] = useState(false);
  const [isMetaAdLibraryGalleryOpen, setIsMetaAdLibraryGalleryOpen] =
    useState(false);
  const [isSubtitleOptionsModalOpen, setIsSubtitleOptionsModalOpen] =
    useState(false);
  const [isAdvancedConfigOpen, setIsAdvancedConfigOpen] = useState(
    defaultAdvancedAccordionExpanded
  );
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [showLogo, setShowLogo] = useState(true);
  const { t } = useTranslationApp();
  const { onTryOpenCompetitorScope } = useCompetitorScopeGuard({
    brandInformation,
  });

  const imagesUrl = watch("imagesUrl") || [];
  const maxReferenceImages = getCreateWithIAReferenceImageLimit({ mediaType });
  const remainingReferenceImageSlots = getRemainingReferenceImageSlots({
    currentCount: imagesUrl.length,
    mediaType,
  });

  const onOpenReferenceImagesFromUrl = useCallback(() => {
    const websiteUrl = getValues("websiteUrl");
    if (!websiteUrl || imagesUrl.length >= maxReferenceImages) {
      return;
    }
    setIsUrlGalleryOpen(true);
  }, [getValues, imagesUrl.length, maxReferenceImages]);

  const onOpenMetaAdLibraryGallery = useCallback(() => {
    onTryOpenCompetitorScope({
      onOpen: () => setIsMetaAdLibraryGalleryOpen(true),
    });
  }, [onTryOpenCompetitorScope]);

  // Get subscription info for creatives count
  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const subscriptionPlan = useSelectorApp(
    (state) => state.payments.subscriptionPlan
  );

  const { typeOfPlans } = useSelectorApp((state) => state.payments);
  const planType = typeOfPlans || PLAN_TYPE_PACKAGES;

  // Calculate remaining creatives
  const showRestCreatives =
    subscriptionPlan ||
    (!subscriptionPlan && subscriptionPlanInfo?.benefits?.creatives > 0);
  const showRestVideos =
    subscriptionPlan ||
    (!subscriptionPlan && subscriptionPlanInfo?.benefits?.videos > 0);

  const restCreatives = subscriptionPlanInfo?.usage?.creatives || 0;
  const restVideos = subscriptionPlanInfo?.usage?.videos || 0;
  const showRemainingMediaBalance =
    planType === PLAN_TYPE_PACKAGES &&
    (mediaType === "video" ? showRestVideos : showRestCreatives);

  const hasText = watch("hasText");
  const hasSubtitles = watch("hasSubtitles");
  const hasWatermark = watch("hasWatermark");
  const prompt = watch("prompt");
  const selectedLanguage = watch("language");
  const numberOfImages = watch("numberOfImages");

  const isServiceBrand = businessOffering === BUSINESS_OFFERING_SERVICES;
  const isServiceVideo = isServiceBrand && mediaType === "video";

  // Check if prompt exceeds character limit
  const isPromptTooLong =
    prompt &&
    prompt.length > MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT;

  // Check if carousel images count exceeds maximum
  const isCarouselCountExceeded =
    mediaType === "carousel" && numberOfImages && Number(numberOfImages) > 10;
  // Function to get accent options based on the selected language
  const getAccentOptions = () => {
    if (!selectedLanguage || !GENERIC_ACCENTS[selectedLanguage]) {
      return [];
    }

    return GENERIC_ACCENTS[selectedLanguage].map((accent) => ({
      value: accent.value,
      label: accent.labelKey, // The Select will translate this labelKey when doTranslate=true
    }));
  };

  // Service videos always include a closing ad; logo upload is optional.
  useEffect(() => {
    if (isServiceVideo) {
      setShowLogo(true);
      setValue("hasLogo", true);
      return;
    }

    const hasLogoValue = !!logoUrl;
    setShowLogo(hasLogoValue);
    setValue("hasLogo", hasLogoValue);
  }, [logoUrl, isServiceVideo, setValue]);

  useEffect(() => {
    if (defaultAdvancedAccordionExpanded) {
      setIsAdvancedConfigOpen(true);
    }
  }, [defaultAdvancedAccordionExpanded]);

  // Set default accent when the selected language changes
  useEffect(() => {
    if (mediaType !== "video") return;

    if (selectedLanguage === "en" || selectedLanguage === "es") {
      setValue("accent", getDefaultAccentForLanguage(selectedLanguage));
    } else {
      setValue("accent", null);
    }
  }, [selectedLanguage, mediaType, setValue]);

  const textOnCreativeLanguageCodes =
    mediaType === "video"
      ? MODAL_CREATE_WITH_IA_TEXT_LANGUAGE_CODES_VIDEO
      : MODAL_CREATE_WITH_IA_TEXT_LANGUAGE_CODES_IMAGE;

  const languageOptionsForTextOnCreative = useMemo(
    () =>
      optionsLanguage.filter((l) =>
        textOnCreativeLanguageCodes.includes(l.value)
      ),
    [textOnCreativeLanguageCodes]
  );

  useEffect(() => {
    if (!hasText) return;
    if (!selectedLanguage) return;
    if (textOnCreativeLanguageCodes.includes(selectedLanguage)) return;
    setValue("language", textOnCreativeLanguageCodes[0] ?? "");
    setValue("accent", getDefaultAccentForLanguage("en"));
  }, [
    hasText,
    mediaType,
    selectedLanguage,
    setValue,
    textOnCreativeLanguageCodes,
  ]);

  // Set isCarousel when mediaType changes to/from carousel
  useEffect(() => {
    if (mediaType === "carousel") {
      setValue("isCarousel", true);
    } else if (mediaType !== "image") {
      setValue("isCarousel", false);
      setValue("numberOfImages", "");
    }
  }, [mediaType, setValue]);

  useEffect(() => {
    if (!logoUrl) return;

    const extension = getExtensionFile(logoUrl);
    if (!extension) return;

    if (!LOGO_IMAGE_FORMATS.includes(extension)) {
      if (!isServiceVideo) {
        setValue("hasLogo", false);
      }
      onChangeBrandInformation((prev) => ({ ...prev, logoUrl: "" }));
    }

    //eslint-disable-next-line
  }, []);

  return (
    <Box
      id={CONFIG_PANEL_SCROLL_CONTAINER_ID}
      sx={{
        width: { xs: "100%", sm: 350 },
        maxWidth: { xs: "100%", sm: 350 },
        bgcolor: "background.paper",
        borderRight: { xs: 0, sm: "1px solid rgba(0, 0, 0, 0.12)" },
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, sm: 3 },
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        position: "relative",
        height: "100%",
        minWidth: 0, // Allow shrinking below content size
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          position: "relative",
          pr: isSmallScreen ? 5 : 0, // Add padding right when hamburger is visible
        }}
      >
        <Box
          sx={{
            p: 1.5,
            bgcolor: "primary.main",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SparklesIcon sx={{ color: "#ffffff !important", fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {" "}
          {/* Allow text to shrink */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t(MODAL_CREATE_WITH_IA_TITLE)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t(MODAL_CREATE_WITH_IA_SUBTITLE)}
          </Typography>
        </Box>
        {isSmallScreen && (
          <IconButton
            onClick={onToggleConfig}
            size="small"
            sx={{
              position: "absolute",
              top: "45%",
              right: 0,
              transform: "translateY(-50%)",
              zIndex: 3,
              "&:hover": {
                bgcolor: "transparent",
              },
            }}
            aria-label="Cerrar configuración"
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* Basic Configuration */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon sx={{ fontSize: 19, color: "text.secondary" }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t(MODAL_CREATE_WITH_IA_CONFIGURATION_BASIC)}
          </Typography>
        </Box>

        <Select
          name="mediaType"
          control={control}
          variant="filled"
          options={MODAL_CREATE_WITH_IA_MEDIA_TYPES}
          label={t(MODAL_CREATE_WITH_IA_LABEL_MEDIA_TYPE)}
          fullWidth
          size="small"
          doTranslate={true}
          errors={errors}
        />

        <Select
          name="aspectRatio"
          control={control}
          variant="filled"
          options={
            mediaType === "video"
              ? MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO
              : MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE
          }
          label={t(ASPECT_RATIO_PLACEHOLDER)}
          fullWidth
          size="small"
          doTranslate={true}
          errors={errors}
        />

        {mediaType === "video" && (
          <>
            <Select
              name="videoType"
              control={control}
              variant="filled"
              options={
                businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT
                  ? optionsVideoTypesForPhysicalProduct
                  : optionsVideoTypesForServices
              }
              label={t(MODAL_CREATE_WITH_IA_LABEL_VIDEO_TYPE)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
            />
            <Select
              name="duration"
              control={control}
              variant="filled"
              options={[
                {
                  value: 20,
                  label: MODAL_CREATE_WITH_IA_VIDEO_DURATION_20_SECONDS,
                },
                {
                  value: 30,
                  label: MODAL_CREATE_WITH_IA_VIDEO_DURATION_30_SECONDS,
                },
              ]}
              label={t(MODAL_CREATE_WITH_IA_LABEL_VIDEO_DURATION_SECONDS)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
            />
          </>
        )}

        {(mediaType === "image" || mediaType === "carousel") && (
          <>
            <Select
              name="platform"
              control={control}
              variant="filled"
              options={MODAL_CREATE_WITH_IA_PLATFORMS}
              label={t(MODAL_CREATE_WITH_IA_LABEL_PLATFORM)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
              rules={{ required: false }}
            />

            {/* Number of images input - show when mediaType is image or carousel */}
            {(mediaType === "image" || mediaType === "carousel") && (
              <TextField
                control={control}
                name="numberOfImages"
                variant="filled"
                label={t(MODAL_CREATE_WITH_IA_LABEL_NUMBER_OF_IMAGES)}
                placeholder={t(
                  MODAL_CREATE_WITH_IA_PLACEHOLDER_NUMBER_OF_IMAGES
                )}
                fullWidth
                size="small"
                type="number"
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                  min: {
                    value: 1,
                    message: t(FIELD_NUMBER_0_OR_NEGATIVE),
                  },
                  max: {
                    value: 10,
                    message: t(FIELD_NUMBER_GREATER_THAN, { value: 10 }),
                  },
                  validate: (value) => {
                    const numValue = Number(value);
                    if (
                      isNaN(numValue) ||
                      numValue < 1 ||
                      !Number.isInteger(numValue)
                    ) {
                      return t(FIELD_NUMBER_0_OR_NEGATIVE);
                    }
                    if (numValue > 10) {
                      return t(FIELD_NUMBER_GREATER_THAN, { value: 10 });
                    }
                    return true;
                  },
                }}
              />
            )}
          </>
        )}

        {/* Text in image toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {mediaType === "video" ? t(VIDEO_LANGUAGE) : t(TEXT_IN_IMAGE)}
          </Typography>
          {(mediaType === "image" || mediaType === "carousel") && (
            <Switch
              id="has-text-switch"
              isActive={!!hasText}
              size="small"
              hasLabel={false}
              onChangeIsActive={(checked) => setValue("hasText", checked)}
            />
          )}
        </Box>

        {hasText && (
          <AutocompleteField
            name="language"
            control={control}
            variant="filled"
            options={languageOptionsForTextOnCreative}
            label={t(MODAL_CREATE_WITH_IA_LABEL_LANGUAGE)}
            fullWidth
            size="small"
            disableClearable={true}
            doTranslate={true}
            errors={errors}
          />
        )}

        {/* Select de acentos - solo para videos y cuando el idioma es en o es */}
        {mediaType === "video" &&
          (selectedLanguage === "en" || selectedLanguage === "es") &&
          GENERIC_ACCENTS[selectedLanguage] &&
          getAccentOptions().length > 0 && (
            <Select
              name="accent"
              control={control}
              variant="filled"
              options={getAccentOptions()}
              label={t(MODAL_CREATE_WITH_IA_LABEL_ACCENT)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
            />
          )}

        {mediaType === "video" && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t(MODAL_CREATE_WITH_IA_LABEL_WITH_SUBTITLES)}
              </Typography>
              <Switch
                id="has-subtitles-switch"
                isActive={!!hasSubtitles}
                size="small"
                hasLabel={false}
                onChangeIsActive={(checked) =>
                  setValue("hasSubtitles", checked)
                }
              />
            </Box>
            {hasSubtitles && (
              <Typography
                component="span"
                onClick={() => setIsSubtitleOptionsModalOpen(true)}
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "primary.main",
                  mt: 0.5,
                  display: "inline-block",
                  "&:hover": { color: "primary.dark" },
                }}
              >
                {t(MODAL_SUBTITLE_OPTIONS_PERSONALIZE)}
              </Typography>
            )}
          </Box>
        )}

        {mediaType === "video" && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t(MODAL_CREATE_WITH_IA_LABEL_AI_WATERMARK)}
              </Typography>
              <Switch
                id="has-watermark-switch"
                isActive={!!hasWatermark}
                size="small"
                hasLabel={false}
                onChangeIsActive={(checked) =>
                  setValue("hasWatermark", checked)
                }
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: 12,
                lineHeight: 1.6,
                mt: 0.5,
              }}
            >
              {t(MODAL_CREATE_WITH_IA_AI_WATERMARK_DESCRIPTION)}
            </Typography>
          </Box>
        )}

        {/* Logo toggle — images, carousel, and non-service video */}
        {(mediaType === "image" ||
          mediaType === "carousel" ||
          (mediaType === "video" && !isServiceVideo)) && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: mediaType === "video" ? 700 : 600,
                  color: "text.primary",
                }}
              >
                {t(mediaType === "video" ? LOGO_IN_VIDEO : LOGO_IN_IMAGE)}
              </Typography>
              <Switch
                id="has-logo-switch"
                isActive={showLogo}
                size="small"
                hasLabel={false}
                onChangeIsActive={(checked) => {
                  setShowLogo(checked);
                  setValue("hasLogo", checked);
                }}
              />
            </Box>

            {showLogo && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.6 }}
              >
                {mediaType === "video"
                  ? `${t(LOGO_IN_VIDEO_DESCRIPTION_LEAD)} ${t(LOGO_DESCRIPTION)}`
                  : t(LOGO_DESCRIPTION)}
              </Typography>
            )}

            {/* Show logo upload section when toggle is active */}
            {showLogo && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 1,
                  mb: 1,
                }}
              >
                {logoUrl && (
                  <CardMedia
                    url={logoUrl}
                    sx={{
                      objectFit: "cover",
                      borderRadius: 3,
                      height: 120,
                      width: 120,
                      mb: 1,
                    }}
                    sxImage={{
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                    }}
                  />
                )}
                <UploadFileButton
                  id="upload-logo-url"
                  textUpload={BUTTON_UPLOAD_LOGO}
                  colorUpload="secondary"
                  textRemove={BUTTON_CHANGE_LOGO}
                  colorRemove="secondary"
                  disableVerificationMedia={true}
                  value={logoUrl}
                  onChange={async (value) => {
                    const url = value?.[0];
                    if (onSaveLogo) {
                      await onSaveLogo(url);
                    }
                  }}
                  pathStorage={`socialMedia/${userID}/`}
                  acceptedFormats={LOGO_IMAGE_FORMATS}
                  modeUpload="sync"
                  loading={isUploadingLogo}
                  onCallbackLoading={(loading) => setIsUploadingLogo(loading)}
                  sx={{
                    fontSize: 13,
                    height: 40,
                    width: 110,
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Service video: optional logo for closing ad */}
        {isServiceVideo && (
          <Box id={SERVICE_VIDEO_LOGO_SECTION_ID} sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {t(LOGO_IN_VIDEO)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: 12,
                lineHeight: 1.6,
                mt: 0.5,
              }}
            >
              {logoUrl
                ? `${t(LOGO_IN_VIDEO_DESCRIPTION_LEAD)} ${t(LOGO_DESCRIPTION)}`
                : t(
                    MODAL_CREATE_WITH_IA_SERVICE_VIDEO_LOGO_OPTIONAL_DESCRIPTION
                  )}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 1,
                mb: 1,
              }}
            >
              {logoUrl && (
                <CardMedia
                  url={logoUrl}
                  sx={{
                    objectFit: "cover",
                    borderRadius: 3,
                    height: 120,
                    width: 120,
                    mb: 1,
                  }}
                  sxImage={{
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                  }}
                />
              )}
              <UploadFileButton
                id="upload-service-video-logo-url"
                textUpload={BUTTON_UPLOAD_LOGO}
                colorUpload="secondary"
                textRemove={BUTTON_CHANGE_LOGO}
                colorRemove="secondary"
                disableVerificationMedia={true}
                value={logoUrl}
                onChange={async (value) => {
                  const url = value?.[0];
                  if (onSaveLogo) {
                    await onSaveLogo(url);
                  }
                }}
                pathStorage={`socialMedia/${userID}/`}
                acceptedFormats={LOGO_IMAGE_FORMATS}
                modeUpload="sync"
                loading={isUploadingLogo}
                onCallbackLoading={(loading) => setIsUploadingLogo(loading)}
                sx={{
                  fontSize: 13,
                  height: 40,
                  width: 110,
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Show here only for physical products */}
      {businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT && (
        <Box id="create-with-ia-reference-images-section">
          <ReferenceImageSelector
            control={control}
            errors={errors}
            onExtractFromUrl={onOpenReferenceImagesFromUrl}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onOpenGallery={onOpenGallery}
            businessOffering={businessOffering}
            mediaType={mediaType}
          />
        </Box>
      )}

      {/* Advanced Configuration */}
      <Accordion
        expanded={isAdvancedConfigOpen}
        onChange={() => setIsAdvancedConfigOpen(!isAdvancedConfigOpen)}
        sx={{
          boxShadow: "none",
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: 0,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            p: 0,
            minHeight: 40,
            "&.Mui-expanded": {
              minHeight: 40,
            },
            "& .MuiAccordionSummary-content": {
              margin: "8px 0",
              "&.Mui-expanded": {
                margin: "8px 0",
              },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SettingsIcon sx={{ fontSize: 19, color: "text.secondary" }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(MODAL_CREATE_WITH_IA_CONFIGURATION_ADVANCED)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: "0 0 16px 0",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {businessOffering === BUSINESS_OFFERING_SERVICES ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {(mediaType === "image" || mediaType === "carousel") && (
                <Box id={REFERENCE_AD_SECTION_ID}>
                  <ReferenceAdSelector
                    setValue={setValue}
                    watch={watch}
                    onOpenGallery={onOpenGalleryForReferenceAd}
                    onImportFromMetaAdLibrary={onOpenMetaAdLibraryGallery}
                  />
                </Box>
              )}
              <ReferenceImageSelector
                control={control}
                errors={errors}
                onExtractFromUrl={onOpenReferenceImagesFromUrl}
                setValue={setValue}
                getValues={getValues}
                watch={watch}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onOpenGallery={onOpenGallery}
                businessOffering={businessOffering}
                mediaType={mediaType}
              />
            </Box>
          ) : (
            (mediaType === "image" || mediaType === "carousel") && (
              <Box id={REFERENCE_AD_SECTION_ID}>
                <ReferenceAdSelector
                  setValue={setValue}
                  watch={watch}
                  onOpenGallery={onOpenGalleryForReferenceAd}
                  onImportFromMetaAdLibrary={onOpenMetaAdLibraryGallery}
                />
              </Box>
            )
          )}
          {/* Localización - solo para imágenes y carrusel */}
          {(mediaType === "image" || mediaType === "carousel") && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t(MODAL_CREATE_WITH_IA_LABEL_LOCATION)}
              </Typography>
              <AutocompleteField
                control={control}
                name="imageLocation"
                options={COUNTRIES}
                doTranslate
                fullWidth
                variant="filled"
                placeholder={t(SECTION_COUNTRY_DEFAULT_VALUE)}
                errors={errors}
                rules={{ required: false }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(MODAL_CREATE_WITH_IA_CONFIGURATION_INSTRUCTIONS)}
            </Typography>
            <TextField
              control={control}
              name="prompt"
              multiline
              minRows={4}
              maxRows={6}
              placeholder={t(MODAL_CREATE_WITH_IA_PROMPT_OPTIONAL)}
              fullWidth
              variant="filled"
              errors={errors}
              showCounterCharacters={true}
              maxCharacters={
                MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT
              }
              rules={{
                maxLength: {
                  value: MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT,
                  message: t(FIELD_NUMBER_GREATER_THAN, {
                    value:
                      MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT,
                  }),
                },
                required: false,
              }}
            />
          </Box>

          {/* Business Look & Feel field below description */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(BUSINESS_LOOK_AND_FEEL_PLACEHOLDER)}
            </Typography>
            <TextField
              control={control}
              name="businessLookAndFeel"
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              variant="filled"
              errors={errors}
              rules={{ required: false }}
            />
          </Box>

          {mediaType !== "video" &&
            brandInformation?.hasAdditionalAssets === true && (
              <AdditionalAssetsToggleSection
                assets={additionalAssets}
                enabledMap={additionalAssetsEnabledMap}
                onToggleAsset={onToggleAdditionalAsset}
              />
            )}
        </AccordionDetails>
      </Accordion>

      {/* Generate Button - Fixed at the bottom */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          bgcolor: "background.paper",
          pt: 2,
          pb: 2,
          mt: "auto",
          zIndex: 6,
        }}
      >
        {/* Creatives remaining message */}
        {showRemainingMediaBalance && (
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "text.secondary",
              mb: 1,
              fontSize: { xs: 13, sm: 13 },
              opacity: 0.8,
            }}
          >
            {t(
              mediaType === "video"
                ? restVideos === 1
                  ? VIDEOS_REMAINING_SHORT_SINGULAR
                  : VIDEOS_REMAINING_SHORT
                : restCreatives === 1
                  ? CREATIVES_REMAINING_SHORT_SINGULAR
                  : CREATIVES_REMAINING_SHORT,
              {
                value:
                  mediaType === "video"
                    ? restVideos > 0
                      ? restVideos
                      : 0
                    : restCreatives > 0
                      ? restCreatives
                      : 0,
              }
            )}
          </Typography>
        )}
        <Button
          color="primary"
          onClick={() => {
            onSubmit();
            if (isSmallScreen && onToggleConfig) {
              onToggleConfig();
            }
          }}
          disabled={
            isGenerating ||
            isGalleryOpen ||
            isPromptTooLong ||
            isCarouselCountExceeded
          }
          variant="contained"
          fullWidth
          size="large"
          startIcon={
            isGenerating ? (
              <CircularProgress size={{ xs: 16, sm: 20 }} color="inherit" />
            ) : (
              <SparklesIcon
                sx={{
                  color: "#ffffff !important",
                  fontSize: { xs: 12, sm: 16 },
                }}
              />
            )
          }
          sx={{
            height: { xs: 44, sm: 48 },
          }}
        >
          {isGenerating
            ? t(MODAL_CREATE_WITH_IA_GENERATING)
            : t(MODAL_CREATE_WITH_IA_GENERATE_WITH_AI)}
        </Button>
      </Box>

      {/* Modal: Load images from website URL (reference images) */}
      {isUrlGalleryOpen && remainingReferenceImageSlots > 0 && (
        <ModalGalleryFromUrl
          modalOpen={isUrlGalleryOpen}
          onCloseModal={() => setIsUrlGalleryOpen(false)}
          fetchMode={MODAL_GALLERY_FETCH_MODE_URL}
          fetchUrl={getValues("websiteUrl")}
          multiple={true}
          maxSelections={remainingReferenceImageSlots}
          onCallbackSelectedItems={(items) => {
            const current = getValues("imagesUrl") || [];
            const urls = items.map((it) => it.url).filter(Boolean);
            setValue(
              "imagesUrl",
              mergeReferenceImageUrls({
                currentUrls: current,
                newUrls: urls,
                mediaType,
              })
            );
          }}
        />
      )}

      {/* Modal: Meta Ad Library (reference ad, base64 only) */}
      {isMetaAdLibraryGalleryOpen && (
        <ModalCompetitors
          modalOpen={isMetaAdLibraryGalleryOpen}
          onCloseModal={() => setIsMetaAdLibraryGalleryOpen(false)}
          from={MODAL_COMPETITORS_FROM_CREATE_WITH_IA}
          onCallbackSelectedItems={(items) => {
            const selectedUrl = items?.[0]?.url;
            if (selectedUrl) {
              setValue("referenceAdUrl", selectedUrl);
              setIsAdvancedConfigOpen(true);
              scrollToConfigPanelSection({
                sectionId: REFERENCE_AD_SECTION_ID,
              });
            }
          }}
        />
      )}

      {/* Modal: Personalizar opciones de subtítulos */}
      <ModalSubtitleOptions
        modalOpen={isSubtitleOptionsModalOpen}
        onCloseModal={() => setIsSubtitleOptionsModalOpen(false)}
        initialValues={getValues("subtitleOptions")}
        onSave={(options) => {
          setValue("subtitleOptions", options);
        }}
      />
    </Box>
  );
};

export default ConfigurationPanel;
